/**
 * Pure decision helpers for the Restore Confirm Dialog.
 *
 * The dialog is a thin React shell around these functions. All state that
 * depends on user input is derived here so Vitest can cover every button
 * state without spinning up a real DOM:
 *
 *   * Should the "Restore" button be enabled right now?
 *   * What copy should the diff-counts blurb render?
 *   * Is the source snapshot itself restorable (versioning, missing
 *     metadata)?
 *   * Has the user actually typed the exact network name into the
 *     type-to-confirm field?
 */

/**
 * Minimum word-boundary-safe confirm check. We deliberately require an
 * EXACT match (including trailing punctuation) so "my-net" ≠ " my-net ".
 * The UI can call ``String.trim`` on the raw input before passing it in.
 */
export function confirmMatchesNetworkName(typed, networkName) {
  const t = typeof typed === 'string' ? typed.trim() : '';
  const n = typeof networkName === 'string' ? networkName.trim() : '';
  if (!t || !n) return false;
  return t === n;
}

/**
 * Read the integer count buckets out of a preview response without
 * crashing on a malformed payload. Returns zeros for every bucket when the
 * input is missing so the UI can render "No changes" as a neutral state.
 */
export function diffBuckets(preview) {
  const counts = (preview && preview.counts) || {};
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) && x >= 0 ? x : 0;
  };
  return {
    add: n(counts.add),
    update: n(counts.update),
    unchanged: n(counts.unchanged),
    delete: n(counts.delete),
    networkFieldChanges: n(counts.networkFieldChanges),
  };
}

/**
 * Returns true when *some* behavior-affecting change will occur. Used to
 * upgrade the button copy from "Restore (no changes)" → "Restore" and to
 * flip the confirm-gate from optional → mandatory. We intentionally treat
 * ``unchanged`` as a no-op indicator — it never triggers a write path.
 */
export function hasAnyChanges(preview) {
  const b = diffBuckets(preview);
  return b.add + b.update + b.delete + b.networkFieldChanges > 0;
}

/**
 * Single source of truth for the dialog's submit-enabled predicate.
 *
 * Rules:
 *   1. A preview must have loaded. Without it we don't know what the
 *      user is agreeing to.
 *   2. ``loading`` and ``submitting`` disable the button.
 *   3. If there are zero changes the restore is a no-op — we still let
 *      the user click so they can acknowledge the list, but only after
 *      the confirm input matches (the backend also gates on this).
 *   4. When changes DO exist the confirm input must match the network
 *      name. This is the most important rule because it's what stops
 *      a misclick on the snapshot list from triggering an immediate
 *      wipe.
 */
export function canSubmitRestore({
  preview,
  networkName,
  typedConfirm,
  loading,
  submitting,
}) {
  if (loading || submitting) return false;
  if (!preview) return false;
  // Confirmation gate fires in both "changes" and "no-changes" cases —
  // keeps the UI consistent, and the server requires it regardless.
  if (!confirmMatchesNetworkName(typedConfirm, networkName)) return false;
  return true;
}

/**
 * Human-friendly one-liner describing the diff.
 *
 *   "No changes — the network already matches this snapshot."
 *   "1 to add · 2 to update · 1 to delete · 1 setting"
 *   "1 to add" (single bucket)
 */
export function describeDiff(preview) {
  const b = diffBuckets(preview);
  const total = b.add + b.update + b.delete + b.networkFieldChanges;
  if (total === 0) {
    return 'No changes — the network already matches this snapshot.';
  }
  const parts = [];
  if (b.add) parts.push(`${b.add} to add`);
  if (b.update) parts.push(`${b.update} to update`);
  if (b.delete) parts.push(`${b.delete} to delete`);
  if (b.networkFieldChanges) {
    const noun = b.networkFieldChanges === 1 ? 'setting' : 'settings';
    parts.push(`${b.networkFieldChanges} ${noun}`);
  }
  return parts.join(' · ');
}

/**
 * Copy for the soft drift warning on DRIFT_PROMOTE baselines.
 *
 * Creators who deployed a network BEFORE the baseline feature shipped
 * won't have a true pristine "brain-authored" baseline; the best we can
 * do is pin whatever the current state was the first time they opened the
 * versions panel. The warning makes that explicit so they understand why
 * their "golden" feels off.
 */
export function baselineDriftWarning(baselineSource) {
  if (baselineSource === 'DRIFT_PROMOTE') {
    return (
      'This baseline was captured after the network was first deployed, ' +
      'so it may reflect edits made before the version history was ' +
      'available. Cut a new release if you want a pristine snapshot.'
    );
  }
  return '';
}

/**
 * Compose the final restore request body, delegating to the API helper.
 * Exposed here as a pure alias so the UI can validate before enabling the
 * button; the actual network call still goes through
 * ``services/networkRestoreApi``.
 */
export function composeRestorePayload({ source, confirm }) {
  return { source, confirm };
}
